import type { Driver } from '@multirx/shared';
import { effectiveCorner, effectiveCombativeness } from '@multirx/shared/types/driver.js';
import { clampChampionshipStarLevel } from '@multirx/shared/game/starLadder.js';
import { mountInteractiveStarsDriver } from '../components/starRating.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { mountEntityImageBlock } from '../ui/entityImageBlock.js';
import {
  mountGarageEntityRename,
  normalizeGarageDriverNamePart,
  normalizeGarageDriverNationality,
  MAX_GARAGE_DRIVER_NAME_PART_LENGTH,
} from '../garage/garageEntityRename.js';
import {
  NATIONALITIES,
  getFlagEmoji,
  getNationalityLabel,
  normalizeCountryCode,
} from '@multirx/shared/types/nationalities.js';
import {
  assignDriverImageFromLibrary,
  updateDriver,
  uploadDriverImage,
  assignDriverCar,
} from './api.js';
import { fetchCars } from '../cars/api.js';
import type { Car } from '@multirx/shared';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { applyRoleVisibility } from '../auth/navAccount.js';
import { resolveMediaUrl } from '../mediaUrl.js';
import { mountCar3dPreview, type Car3dPreviewHandle } from '../cars/car3dPreview.js';
import { resolveCar3dModelUrl } from '../cars/car3dModelMap.js';
import { enhanceMenuSelect } from '../ui/menuSelect.js';
import { cacheBustCarGlbUrl } from '../cars/carOrientationFix.js';

export type DriverDetailHandlers = {
  onSaved?: (updated: Driver) => void;
  onDelete?: (driver: Driver) => void;
};

let activeAssignedCar3d: Car3dPreviewHandle | null = null;
let activeHoverCar3d: Car3dPreviewHandle | null = null;

function clearHoverCar3d(previewHost?: HTMLElement): void {
  activeHoverCar3d?.dispose();
  activeHoverCar3d = null;
  if (previewHost) previewHost.replaceChildren();
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function clampDriverStat(v: number): number {
  return clampChampionshipStarLevel(v);
}

function buildDriverNationalityOptions(
  currentCode: string
): Array<{ value: string; label: string }> {
  const options = NATIONALITIES.map((entry) => ({
    value: entry.code,
    label: `${entry.flagEmoji} ${entry.label}`,
  }));
  const current = normalizeCountryCode(currentCode);
  if (current && !options.some((option) => option.value === current)) {
    options.unshift({
      value: current,
      label: `${getFlagEmoji(current)} ${getNationalityLabel(current)}`,
    });
  }
  return options;
}

function normalizeHandlers(
  handlers?: DriverDetailHandlers | ((updated: Driver) => void)
): DriverDetailHandlers {
  if (typeof handlers === 'function') return { onSaved: handlers };
  return handlers ?? {};
}

function renderAssignedCarMedia(host: HTMLElement, car: Car | null): void {
  activeAssignedCar3d?.dispose();
  activeAssignedCar3d = null;
  host.innerHTML = '';

  if (!car) {
    host.innerHTML = `<p class="driver-assigned-car-empty">Aucune voiture attribuée</p>`;
    return;
  }

  const imageUrl = resolveMediaUrl(car.imagePath);
  const media = document.createElement('div');
  media.className = 'driver-assigned-car-media';
  media.innerHTML = `
    <div class="driver-assigned-car-photo">
      ${
        imageUrl
          ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(car.name)}" loading="lazy" />`
          : `<span class="driver-assigned-car-photo-placeholder">Pas d’image</span>`
      }
    </div>
    <div class="driver-assigned-car-3d car-detail-3d" data-driver-car-3d aria-hidden="true"></div>
  `;
  host.appendChild(media);

  const preview3d = media.querySelector<HTMLElement>('[data-driver-car-3d]');
  if (preview3d) {
    activeAssignedCar3d = mountCar3dPreview(
      preview3d,
      cacheBustCarGlbUrl(resolveCar3dModelUrl(car))
    );
  }
}

export function renderDriverDetail(
  container: HTMLElement,
  driver: Driver | null,
  handlers?: DriverDetailHandlers | ((updated: Driver) => void)
): void {
  const { onSaved, onDelete } = normalizeHandlers(handlers);
  activeAssignedCar3d?.dispose();
  activeAssignedCar3d = null;
  clearHoverCar3d();
  container.innerHTML = '';

  if (driver === null) {
    container.innerHTML = `
      <div class="circuits-detail-empty">
        <p>Sélectionnez un pilote dans la liste pour afficher son détail.</p>
      </div>`;
    return;
  }

  const cornerEff = effectiveCorner(driver);
  const combatEff = effectiveCombativeness(driver);
  const section = document.createElement('div');
  section.className = 'circuits-detail circuits-detail--driver car-detail';
  section.innerHTML = `
    <header class="circuits-detail-header car-detail-header">
      <div class="car-detail-header-left circuits-detail-header-main">
        <div class="circuits-detail-title-row">
          <h2 class="circuits-detail-title">#${driver.id} · ${escapeHtml(labelOwnDriver(`${driver.firstName} ${driver.lastName}`.trim(), driver.id))} ${nationalityFlagMarkup(driver.nationality)}</h2>
          <button type="button" class="circuits-detail-rename-trigger" aria-label="modifier le pilote" title="Modifier">✎</button>
        </div>
        <span class="circuits-detail-muted">Total ${(driver.pilotage ?? 0) + (driver.defense ?? 0)}★</span>
        <p class="circuits-detail-rename-status" role="status" hidden></p>
      </div>
    </header>
    <div class="driver-detail-layout">
      <div class="driver-detail-col driver-detail-col--portrait" data-driver-portrait></div>
      <div class="driver-detail-col driver-detail-col--info">
        <p class="detail-effective-bonuses">Bonus course (saison) : virage <strong>+${cornerEff}</strong> · combativité <strong>+${combatEff}</strong></p>
        <div class="detail-edit-form" data-driver-id="${driver.id}">
          <div class="detail-form-loader" aria-hidden="true">
            <span class="spinner"></span>
            <span>Sauvegarde…</span>
          </div>
          <div class="detail-edit-row" data-stat="pilotage" data-current-value="${driver.pilotage}">
            <label>Pilotage (virage)</label>
            <span class="star-display"></span>
          </div>
          <div class="detail-edit-row" data-stat="defense" data-current-value="${driver.defense}">
            <label>Défense (combativité)</label>
            <span class="star-display"></span>
          </div>
          <div class="detail-edit-row driver-car-assign" data-role="admin">
            <label for="driver-assigned-car">Voiture attribuée</label>
            <div class="driver-assigned-car-preview" data-driver-car-preview></div>
            <select id="driver-assigned-car" class="detail-select">
              <option value="">Chargement…</option>
            </select>
          </div>
        </div>
      </div>
    </div>`;

  const reloadDetail = (saved: Driver): void => {
    onSaved?.(saved);
    renderDriverDetail(container, saved, handlers);
  };

  const portraitHost = section.querySelector<HTMLElement>('[data-driver-portrait]');
  if (driver.id != null && portraitHost) {
    mountEntityImageBlock(section, {
      entityId: driver.id,
      collection: 'drivers',
      imagePath: driver.imagePath,
      alt: `${driver.firstName} ${driver.lastName}`,
      pickerTitle: 'Choisir un avatar pilote',
      mountIn: portraitHost,
      handlers: {
        uploadFromPc: (file) => uploadDriverImage(driver.id!, file),
        assignFromLibrary: (publicPath) => assignDriverImageFromLibrary(driver.id!, publicPath),
      },
      onAfterSave: (saved) => reloadDetail(saved as Driver),
    });
  }

  const form = section.querySelector('.detail-edit-form') as HTMLElement;
  const rows = section.querySelectorAll('.detail-edit-row[data-stat]');
  const carPreviewHost = section.querySelector<HTMLElement>('[data-driver-car-preview]');

  function getCurrentDriver(): Driver {
    const d = driver as Driver;
    const data: Record<string, number> = {
      pilotage: d.pilotage,
      defense: d.defense,
    };
    rows.forEach((row) => {
      const stat = (row as HTMLElement).dataset.stat!;
      const val = (row as HTMLElement).dataset.currentValue;
      if (val != null) data[stat] = clampDriverStat(Number(val));
    });
    return {
      id: d.id,
      firstName: d.firstName,
      lastName: d.lastName,
      nationality: d.nationality,
      pilotage: data.pilotage,
      defense: data.defense,
      imagePath: d.imagePath,
      division: d.division,
      defaultCarId: d.defaultCarId ?? null,
    };
  }

  function saveDriver(): void {
    const updated = getCurrentDriver();
    if (updated.id == null) return;
    form.classList.add('detail-edit-form--saving');
    updateDriver(updated.id, {
      firstName: updated.firstName,
      lastName: updated.lastName,
      nationality: updated.nationality,
      pilotage: updated.pilotage,
      defense: updated.defense,
      imagePath: updated.imagePath ?? null,
      division: updated.division,
      defaultCarId: updated.defaultCarId ?? null,
    })
      .then((saved) => reloadDetail(saved))
      .catch(() => {
        form.classList.remove('detail-edit-form--saving');
      });
  }

  rows.forEach((row) => {
    const stat = (row as HTMLElement).dataset.stat!;
    const display = row.querySelector('.star-display') as HTMLElement;
    const initial = clampDriverStat(
      Number((row as HTMLElement).dataset.currentValue ?? driver[stat as keyof Driver])
    );
    if (display) {
      mountInteractiveStarsDriver(display, initial, (value) => {
        (row as HTMLElement).dataset.currentValue = String(value);
        saveDriver();
      });
    }
  });

  const carSelect = section.querySelector('#driver-assigned-car') as HTMLSelectElement;
  if (driver.id != null && carSelect) {
    const carsById = new Map<number, Car>();
    enhanceMenuSelect(carSelect, {
      onOptionHover: ({ value, previewHost }) => {
        clearHoverCar3d(previewHost);
        if (value == null || value === '') {
          previewHost.innerHTML =
            '<p class="menu-select__hover-preview-empty">Survolez une voiture</p>';
          return;
        }
        const carId = Number(value);
        const car = Number.isFinite(carId) ? (carsById.get(carId) ?? null) : null;
        if (!car) {
          previewHost.innerHTML =
            '<p class="menu-select__hover-preview-empty">Aperçu indisponible</p>';
          return;
        }
        activeHoverCar3d = mountCar3dPreview(
          previewHost,
          cacheBustCarGlbUrl(resolveCar3dModelUrl(car)),
          undefined,
          { snapshot: true }
        );
      },
    });
    const division = driver.division ?? 1;
    fetchCars(200)
      .then((cars: Car[]) => {
        const sameDiv = cars.filter((c) => (c.division ?? 1) === division && c.id != null);
        carsById.clear();
        for (const c of sameDiv) {
          if (c.id != null) carsById.set(c.id, c);
        }
        const currentId = driver.defaultCarId ?? null;
        const assigned = sameDiv.find((c) => c.id === currentId) ?? null;
        carSelect.innerHTML =
          `<option value="">— Aucune —</option>` +
          sameDiv
            .map((c) => {
              const selected = c.id === currentId ? ' selected' : '';
              return `<option value="${c.id}"${selected}>${escapeHtml(c.name)} (#${c.id})</option>`;
            })
            .join('');
        carSelect.dispatchEvent(new Event('menu-select:refresh'));
        if (carPreviewHost) renderAssignedCarMedia(carPreviewHost, assigned);
      })
      .catch(() => {
        carsById.clear();
        carSelect.innerHTML = `<option value="">Erreur chargement</option>`;
        carSelect.dispatchEvent(new Event('menu-select:refresh'));
        if (carPreviewHost) renderAssignedCarMedia(carPreviewHost, null);
      });
    carSelect.addEventListener('change', () => {
      const carId = Number(carSelect.value);
      if (!Number.isFinite(carId) || carId <= 0 || driver.id == null) return;
      carSelect.disabled = true;
      carSelect.dispatchEvent(new Event('menu-select:refresh'));
      assignDriverCar(driver.id, carId)
        .then((saved) => reloadDetail(saved))
        .catch(() => {
          carSelect.disabled = false;
          carSelect.dispatchEvent(new Event('menu-select:refresh'));
        });
    });
  } else if (carPreviewHost) {
    renderAssignedCarMedia(carPreviewHost, null);
  }

  const titleRow = section.querySelector('.circuits-detail-title-row') as HTMLElement;
  const renameBtn = section.querySelector('.circuits-detail-rename-trigger');
  if (onDelete && titleRow && renameBtn) {
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'circuits-detail-delete-btn';
    deleteBtn.setAttribute('aria-label', 'Supprimer');
    deleteBtn.title = 'Supprimer';
    deleteBtn.dataset.role = 'admin';
    deleteBtn.textContent = '🗑';
    deleteBtn.addEventListener('click', () => onDelete(driver));
    renameBtn.after(deleteBtn);
  }
  mountGarageEntityRename({
    section,
    titleRow,
    fields: [
      {
        id: 'firstName',
        label: 'Prénom',
        value: driver.firstName,
        maxLength: MAX_GARAGE_DRIVER_NAME_PART_LENGTH,
        placeholder: 'Prénom',
      },
      {
        id: 'lastName',
        label: 'Nom',
        value: driver.lastName,
        maxLength: MAX_GARAGE_DRIVER_NAME_PART_LENGTH,
        placeholder: 'Nom',
      },
      {
        kind: 'select',
        id: 'nationality',
        label: 'Nationalité',
        value: driver.nationality,
        options: buildDriverNationalityOptions(driver.nationality),
      },
    ],
    onSave: async (values) => {
      const firstName = normalizeGarageDriverNamePart(values.firstName ?? '');
      const lastName = normalizeGarageDriverNamePart(values.lastName ?? '');
      const nationality = normalizeGarageDriverNationality(values.nationality ?? '');
      if (!firstName || !lastName) {
        throw new Error(
          `Prénom et nom requis (1–${MAX_GARAGE_DRIVER_NAME_PART_LENGTH} caractères chacun).`
        );
      }
      if (!nationality) {
        throw new Error('Sélectionnez une nationalité valide.');
      }
      const current = getCurrentDriver();
      if (current.id == null) return;
      const saved = await updateDriver(current.id, {
        firstName,
        lastName,
        nationality,
        pilotage: current.pilotage,
        defense: current.defense,
        imagePath: current.imagePath ?? null,
        division: current.division,
        defaultCarId: current.defaultCarId ?? null,
      });
      reloadDetail(saved);
    },
    onRestore: () => renderDriverDetail(container, driver, handlers),
  });

  container.appendChild(section);
  applyRoleVisibility();
}

export function setDriverDetailLoading(container: HTMLElement, loading: boolean): void {
  if (loading) {
    activeAssignedCar3d?.dispose();
    activeAssignedCar3d = null;
    container.innerHTML =
      '<div class="circuits-loading"><span class="spinner"></span><p>Chargement du pilote…</p></div>';
  }
}

export function setDriverDetailError(container: HTMLElement, message: string): void {
  activeAssignedCar3d?.dispose();
  activeAssignedCar3d = null;
  container.innerHTML = `
    <div class="circuits-error">
      <span class="circuits-error-icon" aria-hidden="true">⚠️</span>
      <p>${escapeHtml(message)}</p>
    </div>`;
}
